/**
 * IMPORTS
 */
import { useEffect, useState } from "react";

import { Download, ImageOff } from "lucide-react";

import { ImagePreviewModal } from "@components/image-preview-modal";

import type { IAnexoMensagem } from "@/interface/conversa-cliente";

type AnexoImagemPreviewProps = {
  anexo: IAnexoMensagem;
  /** Busca o binário com o token (anexos não têm URL pública). */
  carregar: () => Promise<Blob>;
  onBaixar: () => void;
};

type Estado = { url: string | null; erro: boolean };

/**
 * Miniatura da imagem anexada; clique amplia. O object URL é criado ao montar
 * e liberado ao desmontar.
 */
function AnexoImagemPreview({ anexo, carregar, onBaixar }: AnexoImagemPreviewProps) {
  const [estado, setEstado] = useState<Estado>({ url: null, erro: false });
  const [ampliada, setAmpliada] = useState(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: anexo é imutável; recarrega só se trocar de anexo
  useEffect(() => {
    let ativo = true;
    let urlCriada: string | null = null;

    carregar()
      .then((blob) => {
        if (!ativo) return;
        urlCriada = URL.createObjectURL(blob);
        setEstado({ url: urlCriada, erro: false });
      })
      .catch(() => {
        if (ativo) setEstado({ url: null, erro: true });
      });

    return () => {
      ativo = false;
      if (urlCriada) URL.revokeObjectURL(urlCriada);
    };
  }, [anexo.id]);

  if (estado.erro) {
    return (
      <button
        type="button"
        onClick={onBaixar}
        title={`Baixar ${anexo.nome_original}`}
        className="flex h-32 w-40 flex-col items-center justify-center gap-1 rounded-md border border-gray-200 bg-white p-2 text-xs text-gray-500 hover:bg-gray-50"
      >
        <ImageOff className="size-5" />
        <span className="line-clamp-2 break-all text-center">{anexo.nome_original}</span>
      </button>
    );
  }

  if (!estado.url) {
    return (
      <div className="h-32 w-40 animate-pulse rounded-md bg-gray-200">
        <span className="sr-only">Carregando imagem</span>
      </div>
    );
  }

  return (
    <div className="group/imagem relative">
      <button
        type="button"
        onClick={() => setAmpliada(true)}
        title={`Ampliar ${anexo.nome_original}`}
        className="block overflow-hidden rounded-md border border-gray-200 bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-custom900"
      >
        <img
          src={estado.url}
          alt={anexo.nome_original}
          className="h-32 w-40 object-cover transition-transform hover:scale-105"
        />
      </button>
      <button
        type="button"
        onClick={onBaixar}
        aria-label={`Baixar ${anexo.nome_original}`}
        className="absolute bottom-1 right-1 rounded-md bg-black/60 p-1 text-white opacity-0 transition-opacity focus-visible:opacity-100 group-hover/imagem:opacity-100"
      >
        <Download className="size-4" />
      </button>

      <ImagePreviewModal
        open={ampliada}
        onClose={() => setAmpliada(false)}
        imageUrl={estado.url}
        alt={anexo.nome_original}
      />
    </div>
  );
}

/**
 * EXPORTS
 */
export { AnexoImagemPreview };
